import { v4 as uuidv4 } from "uuid";

import { init } from "@elastic/apm-rum";

import AnalyticsSerive from "./analyticService";
import AuthService from "./authService";
import { getEnv } from "./configService";
import { LocalStorage } from "./localStorageService";

type Labels = {
    [key: string]: string | number | boolean;
};

let navigationIndex = 1;
const navigationHistory: Labels = {};

const setEndpointId = (id: string) => {
    LocalStorage.setItem("endpoint", id);
};

const getEndpointId = () => {
    let id = LocalStorage.getItem<string | null>("endpoint");
    if (!id) {
        id = uuidv4();
        setEndpointId(id);
    }

    return id;
};

// Lazy init: pre-refactor, AnalyticsSerive populated its analytics field eagerly during
// module load, so the APM client could be created synchronously at module scope. Post-refactor
// analytics is empty until AnalyticsSerive.initialize() runs (in App.tsx's useEffect), so the
// APM client has to be created on first use. We only cache the successful init — caching a
// null result would poison the slot if a caller hit getApm() before initialize() ran.
let _apm: ReturnType<typeof init> | null = null;
const getApm = () => {
    if (_apm) {
        return _apm;
    }
    const elasticAnalytic = AnalyticsSerive.getElasticSettings();
    if (!elasticAnalytic) {
        return null;
    }
    _apm = init({
        serverUrl: elasticAnalytic.serverUrl,
        serviceName: getEnv().toLowerCase().trimEnd() + "-portal",
        serviceVersion: elasticAnalytic.serviceVersion,
    });
    return _apm;
};

const createTransaction = (name: string, labels: Labels) => {
    const transaction = getApm()?.startTransaction(name, "custom");
    if (transaction) {
        transaction.addLabels(labels);
        transaction.end(new Date().getTime() + 1);
    }
};

const updateUserContext = () => {
    const user = AuthService.getCurrentUser();
    if (user && user.sub !== getEndpointId()) {
        setEndpointId(user.sub);
    }

    const endpoint = getEndpointId();
    const context: UserObject = { id: endpoint };

    const email = user ? user.email : null;
    if (email) {
        context.email = email;
    }

    getApm()?.setUserContext(context);
};

const initApmContext = () => {
    const apm = getApm();
    createTransaction("resolution", {
        resolution: `${window.screen.width}x${window.screen.height}`,
    });

    apm?.observe("transaction:start", (transaction) => {
        // route-change transactions are usually discarded as they have no spans included
        // however, we can force sending them by pretending that no sampling was required
        if (transaction.type === "route-change") {
            transaction.sampled = false;
        }
    });
    apm?.observe("transaction:end", (transaction) => {
        // before sending navigation transactions to the server, we add some additional context
        // this way we can build a history of all pages visited by this user
        if (transaction.type === "page-load" || transaction.type === "route-change") {
            const index = navigationIndex - 1;

            navigationHistory["page"] = navigationHistory[`navigation_history_${index}`];
            navigationHistory[`navigation_history_${navigationIndex}`] = transaction.name;

            // we only transmit data for the first 20 pages to elastic
            if (navigationIndex <= 10) {
                transaction.addLabels(navigationHistory);
            }

            navigationIndex++;
        }
    });
};

const ElasticService = {
    initApmContext,
    updateUserContext,
    createTransaction,
};

export default ElasticService;
